// Lightweight toast system — the console's `useNotify()` (mirrors solya-app's hook
// without the react-toastify dependency).

import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react"

interface Toast {
  id: number
  message: string
  variant: "success" | "error"
}

interface Notify {
  success: (message: string) => void
  error: (message: string) => void
}

const NotifyContext = createContext<Notify | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const push = useCallback((message: string, variant: Toast["variant"]) => {
    const id = nextId++
    setToasts((prev) => [...prev, { id, message, variant }])
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 4000)
  }, [])

  const notify = useMemo<Notify>(
    () => ({
      success: (message) => push(message, "success"),
      error: (message) => push(message, "error")
    }),
    [push]
  )

  return (
    <NotifyContext.Provider value={notify}>
      {children}
      <div className="toast-stack">
        {toasts.map((t) => (
          <output key={t.id} className={`toast toast--${t.variant}`}>
            {t.message}
          </output>
        ))}
      </div>
    </NotifyContext.Provider>
  )
}

export function useNotify(): Notify {
  const ctx = useContext(NotifyContext)
  if (!ctx) throw new Error("useNotify must be used within a ToastProvider")
  return ctx
}
